/**
 * Pure day-state derivation for the driver "My day" board.
 *
 * Consumed by BOTH the native board and the web daily view, so inputs must be
 * plain normalized objects, not query-specific shapes.
 *
 * Normalized clock input shape: `{ isClockedIn, hasClockedOutToday }`.
 * - Native maps it from `getMyClockStatus`:
 *   `isClockedIn` = query `isClockedIn`;
 *   `hasClockedOutToday` = `lastEventAction === CLOCK_EVENT_ACTION.CLOCK_OUT`.
 * - Web maps it from `serviceRun.driverClockSummary`:
 *   `isClockedIn` = `Boolean(clockedInAt && !clockedOutAt)`;
 *   `hasClockedOutToday` = `Boolean(clockedOutAt)`.
 */
import {
	CHECKLIST_PURPOSE,
	MY_DAY_CLOCK_IN_REQUIREMENT,
	MY_DAY_CONFIRMATION_KEY,
	MY_DAY_SELF_DEBRIEF_MODE,
	MACHINE_SERVICE_TYPES,
	POS_TYPE,
	STATUS,
} from '../../constants';
import { shouldMachineBeRepaired } from '../serviceRuns/shouldMachineBeRepaired';
import { shouldMachineBeRestocked } from '../serviceRuns/shouldMachineBeRestocked';
import { isStockConfirmationRequired, shouldConfirmStockLevels } from '../serviceRuns/shouldConfirmStockLevels';
import { getLocalTime } from '../time/time';
import { getChecklistSignOff, getChecklistSignOffDescription } from '../checklist/checklistSignOff';
import { isServiceDocketLabelPrintOnly } from '../pickList/isServiceDocketLabelPrintOnly';
import { serviceDocketInventoryCheckStatus } from '../vending-settings';

export const DRIVER_DAY_PHASE = {
	NOT_STARTED: 'notStarted',
	PREPARING: 'preparing',
	ON_THE_RUN: 'onTheRun',
	STOPS_DONE: 'stopsDone',
	DAY_CLOSED: 'dayClosed',
};

export const MY_DAY_STEP_STATUS = {
	DONE: 'done',
	TODO: 'todo',
	BLOCKED: 'blocked',
	INFO: 'info',
	HIDDEN: 'hidden',
};

const isInventoryConfirmed = inventoryCheck => (
	inventoryCheck === serviceDocketInventoryCheckStatus.CONFIRMED ||
	inventoryCheck === serviceDocketInventoryCheckStatus.CONFIRMED_WITH_CHANGES
);

const machineCanAcceptCash = machine => Boolean(machine?.hasCoinMech || machine?.noteReader?.id);

export const docketRequiresMoneyBagReturn = docket => Boolean(machineCanAcceptCash(docket?.machine) &&
	(docket?.collectCash || docket?.cashBag));

export const DEFAULT_DRIVER_KEY_NAME = '8888';

export const DOCKET_ACCESS_REQUIREMENT_TYPE = {
	KEY: 'key',
	ACCESS_PASS: 'accessPass',
	SIGN_IN: 'signIn',
	EQUIPMENT: 'equipment',
};

export const DOCKET_ACCESS_REQUIREMENT_SCOPE = {
	MACHINE: 'machine',
	LOCATION: 'location',
	PLACE: 'place',
};

/**
 * What a stop needs to get in, in display order: `{ type, scope, label, value }`.
 * `scope` says whose requirement it is (DOCKET_ACCESS_REQUIREMENT_SCOPE);
 * `label` is the full sentence (getDocketAccessRequirementLabels); `value` is
 * the bare item (key name, pass details, PPE, sign-in instructions; empty
 * when the site gave no details).
 */
export const getDocketAccessRequirements = (docket) => {
	const requirements = [];
	const machineKeyName = docket?.machine?.machineKey?.name?.trim();

	// Drivers already carry the standard 8888 key, so My Day lists exceptions only.
	if (machineKeyName && machineKeyName !== DEFAULT_DRIVER_KEY_NAME) {
		requirements.push({
			type: DOCKET_ACCESS_REQUIREMENT_TYPE.KEY,
			scope: DOCKET_ACCESS_REQUIREMENT_SCOPE.MACHINE,
			label: machineKeyName,
			value: machineKeyName,
		});
	}

	if (docket?.machine?.location?.requiresAccessPass) {
		const details = docket.machine.location.accessPassDetails?.trim();
		requirements.push({
			type: DOCKET_ACCESS_REQUIREMENT_TYPE.ACCESS_PASS,
			scope: DOCKET_ACCESS_REQUIREMENT_SCOPE.LOCATION,
			label: `Location access pass${details ? `: ${details}` : ' required'}`,
			value: details || '',
		});
	}

	if (docket?.machine?.location?.requiresSignIn) {
		const instructions = docket.machine.location.signInInstructions?.trim();
		requirements.push({
			type: DOCKET_ACCESS_REQUIREMENT_TYPE.SIGN_IN,
			scope: DOCKET_ACCESS_REQUIREMENT_SCOPE.LOCATION,
			label: `Site sign-in required${instructions ? `: ${instructions}` : ''}`,
			value: instructions || '',
		});
	}

	const locationEquipment = docket?.machine?.location?.requiredEquipment?.trim();
	if (locationEquipment) {
		requirements.push({
			type: DOCKET_ACCESS_REQUIREMENT_TYPE.EQUIPMENT,
			scope: DOCKET_ACCESS_REQUIREMENT_SCOPE.LOCATION,
			label: `Location equipment / PPE: ${locationEquipment}`,
			value: locationEquipment,
		});
	}

	if (docket?.machine?.place?.requiresAccessPass) {
		const details = docket.machine.place.accessPassDetails?.trim();
		requirements.push({
			type: DOCKET_ACCESS_REQUIREMENT_TYPE.ACCESS_PASS,
			scope: DOCKET_ACCESS_REQUIREMENT_SCOPE.PLACE,
			label: `POS access pass${details ? `: ${details}` : ' required'}`,
			value: details || '',
		});
	}

	const placeEquipment = docket?.machine?.place?.requiredEquipment?.trim();
	if (placeEquipment) {
		requirements.push({
			type: DOCKET_ACCESS_REQUIREMENT_TYPE.EQUIPMENT,
			scope: DOCKET_ACCESS_REQUIREMENT_SCOPE.PLACE,
			label: `POS equipment / PPE: ${placeEquipment}`,
			value: placeEquipment,
		});
	}

	return requirements;
};

export const getDocketAccessRequirementLabels = docket => getDocketAccessRequirements(docket).map(requirement => requirement.label);

export const buildAccessPreparationRows = dockets => (dockets || [])
	.map(docket => ({
		docket,
		id: docket.id,
		requirementLabels: getDocketAccessRequirementLabels(docket),
	}))
	.filter(row => row.requirementLabels.length > 0);

export const isDocketDoneForMyDay = (docket, { hasPendingConfirmStockSync = false } = {}) => {
	const isOffline = docket?.machine?.isOffline ?? !docket?.machine?.commDevices?.length;
	const supportsStockConfirmation = shouldConfirmStockLevels({
		machineUsesIngredients: docket?.machine?.usesIngredients,
		isOffline,
	});

	// Ingredient and offline workflows keep their established completion state.
	// Online POS require the same restock, ticket, photo and stock-confirmation
	// outcomes shown by the guided stop flow. A durable local queue entry counts
	// as submitted until the server receives it.
	if (!supportsStockConfirmation) {
		return docket?.status === STATUS.COMPLETE;
	}

	// Older phone builds do not query the expiry signals, so use the server's completion verdict for repair visits.
	if (docket?.machineServiceType === MACHINE_SERVICE_TYPES.REPAIR &&
		docket.hasSomeExpiryDatesToUpdate === undefined && docket.productExpiryCheckSchedules === undefined) {
		return docket.status === STATUS.COMPLETE;
	}

	const requiresRestock = shouldMachineBeRestocked(docket?.machineServiceType);
	const requiresTicketStep = shouldMachineBeRepaired(docket?.machineServiceType) &&
		Number(docket?.driverTicketCount) > 0 &&
		Boolean(docket?.ticketList?.id);

	return Boolean((!isStockConfirmationRequired(docket) || isInventoryConfirmed(docket?.inventoryCheck) || hasPendingConfirmStockSync) &&
		(!requiresRestock || docket?.isRestocked) &&
		(!requiresTicketStep || docket?.ticketsResolved) &&
		(!docket?.requirePhoto || docket?.photoTaken));
};

// "What's next" treats a stop the driver could not complete as behind them; it
// still never counts as done or restocked.
export const isDocketHandledForMyDay = (docket, options) => (
	isDocketDoneForMyDay(docket, options) || Boolean(docket?.couldNotComplete?.at)
);

const getMyDayConfirmation = (run, key) => (run?.myDayConfirmations || []).find(confirmation => confirmation?.key === key);

/**
 * Keys, passes and PPE the run's stops need (site sign-in is read, not carried),
 * as [{ id, docket, requirement }]. The phone sends the ticked ids with the
 * Keys and site access and Return keys confirmations.
 */
export const getRunAccessItems = dockets => (dockets || []).flatMap(docket => getDocketAccessRequirements(docket)
	.filter(requirement => requirement.type !== DOCKET_ACCESS_REQUIREMENT_TYPE.SIGN_IN)
	.map(requirement => ({ id: `${docket.id}:${requirement.scope}:${requirement.type}`, docket, requirement })));

// "Not returned: K12 (Toyota Financial Services), …" for items the driver left
// unticked; null when all were ticked or the app sent no list.
const describeUntickedItems = (prefix, items, confirmation) => {
	if (!Array.isArray(confirmation?.items)) {
		return null;
	}

	const ticked = new Set(confirmation.items);
	const missing = items.filter(item => !ticked.has(item.id));
	return missing.length
		? `${prefix}: ${missing.map(({ docket, requirement }) => (
			`${requirement.label} (${docket.machine?.location?.name || docket.machine?.assetId || 'stop'})`
		)).join(', ')}.`
		: null;
};

const isMyDayStepConfirmed = (run, key) => (
	(run?.myDayConfirmations || []).some(confirmation => confirmation?.key === key)
);

// Product decision: drivers should always debrief; "optional" only relaxes who
// runs the debrief (self vs manager), it never lets the day close without one.
export const isSelfDebriefRequired = mode => (
	(mode || MY_DAY_SELF_DEBRIEF_MODE.OPTIONAL) !== MY_DAY_SELF_DEBRIEF_MODE.OFF
);

// Day-closed semantics for a specific run: the optional→required upgrade only
// applies to runs that went through the guided flow (have confirmations), so
// historical days closed under "optional" mode are not retroactively reopened.
export const isSelfDebriefRequiredForRun = (run, mode) => (
	mode === MY_DAY_SELF_DEBRIEF_MODE.REQUIRED ||
	((run?.myDayConfirmations || []).length > 0 && isSelfDebriefRequired(mode))
);

// Runs already mid-flight when the explicit start confirmation shipped have
// dockets in progress but no stored confirmation — treat those as started.
export const isServiceRunStarted = run => (
	isMyDayStepConfirmed(run, MY_DAY_CONFIRMATION_KEY.SERVICE_RUN_STARTED) ||
	(run?.serviceDockets || []).some(docket => docket?.startedProcessing || docket?.status === STATUS.COMPLETE)
);

export const areAllDocketsComplete = (dockets, isDocketComplete = docket => docket?.status === STATUS.COMPLETE) => (
	(dockets || []).length > 0 &&
	dockets.every(isDocketComplete)
);

const hasSubmissionForPurpose = ({ checklistSubmissions, pendingChecklistPurposes, purpose }) => {
	const submission = (checklistSubmissions || []).find(candidate => Number(candidate?.purpose) === purpose);
	if (submission) {
		return { done: true, pending: false, submission };
	}

	const pending = Boolean(pendingChecklistPurposes && (
		pendingChecklistPurposes.has
			? pendingChecklistPurposes.has(purpose)
			: pendingChecklistPurposes.includes?.(purpose)
	));

	return { done: pending, pending };
};

const docketHasOutstandingPick = docket => (
	!isServiceDocketLabelPrintOnly(docket) &&
	(
		shouldMachineBeRestocked(docket?.machineServiceType) &&
		[STATUS.PENDING, STATUS.PROCESSING].includes(docket?.pickListGenerationStatus)
	) ||
	(!docket?.inventoryPicked && (
		Boolean(docket?.inventoryPartiallyPicked) ||
		Boolean(docket?.pickList?.id) ||
		shouldMachineBeRestocked(docket?.machineServiceType)
	))
);
const docketIsPartiallyPicked = docket => Boolean(docket?.inventoryPartiallyPicked && !docket?.inventoryPicked);

export const buildStartOfDaySteps = ({
	run,
	clockStatus,
	tenantSettings,
	checklistSubmissions,
	pendingChecklistPurposes,
	todayDateKey,
	timezone,
	isDocketComplete,
}) => {
	const dockets = run?.serviceDockets || [];
	const clockRequirement = tenantSettings?.myDayRequireClockIn || MY_DAY_CLOCK_IN_REQUIREMENT.OFF;
	const isClockedIn = Boolean(clockStatus?.isClockedIn);
	// Once every stop is complete and the driver has clocked out today, the day
	// is winding down — clock-in must not re-surface as a "To do" next action
	// (that would read as the board asking them to start a new day). A mid-day
	// clock-out with stops remaining still re-prompts clock-in as before.
	const dayOver = !isClockedIn &&
		Boolean(clockStatus?.hasClockedOutToday) &&
		areAllDocketsComplete(dockets, isDocketComplete);

	const steps = [];

	steps.push({
		key: 'clockIn',
		label: 'Clock in',
		description: isClockedIn
			? 'You are clocked in.'
			: dayOver
				? 'You have clocked out for the day.'
				: 'Clock in on the kiosk when it is available. You can continue your day without it.',
		status: isClockedIn || dayOver ? MY_DAY_STEP_STATUS.DONE : MY_DAY_STEP_STATUS.TODO,
		required: clockRequirement !== MY_DAY_CLOCK_IN_REQUIREMENT.OFF,
		// Attendance stays visibly incomplete without stopping operational work.
		blocksProgress: false,
	});

	const requireBriefAcknowledgement = Boolean(tenantSettings?.myDayRequireBriefAcknowledgement);
	const briefAcknowledged = Boolean(run?.briefAcknowledgedAt);
	// A brief that needs no acknowledgement counts as read once the driver closed it.
	const briefRead = !requireBriefAcknowledgement && isMyDayStepConfirmed(run, MY_DAY_CONFIRMATION_KEY.BRIEF_READ);
	steps.push({
		key: 'brief',
		label: 'Read run brief',
		description: briefAcknowledged
			? 'Brief acknowledged. You can reopen it any time.'
			: briefRead
				? 'Brief read. You can reopen it any time.'
				: 'Read today\'s run brief before you head out.',
		status: !run?.brief
			? MY_DAY_STEP_STATUS.HIDDEN
			: briefAcknowledged || briefRead
				? MY_DAY_STEP_STATUS.DONE
				: requireBriefAcknowledgement ? MY_DAY_STEP_STATUS.TODO : MY_DAY_STEP_STATUS.INFO,
		required: requireBriefAcknowledgement,
	});

	const runOverviewConfirmed = isMyDayStepConfirmed(run, MY_DAY_CONFIRMATION_KEY.RUN_OVERVIEW);
	steps.push({
		key: 'runOverview',
		label: 'Run overview',
		description: runOverviewConfirmed
			? 'Run overview confirmed.'
			: 'Review today\'s stops and route, then confirm.',
		status: !dockets.length
			? MY_DAY_STEP_STATUS.HIDDEN
			: runOverviewConfirmed
				? MY_DAY_STEP_STATUS.DONE
				: MY_DAY_STEP_STATUS.TODO,
		required: true,
		badgeCount: dockets.length || undefined,
		// New warehouse-prep confirmations never pull a driver back once the run
		// is under way (covers runs mid-flight when the confirmations shipped).
		skipWhenRunStarted: true,
	});

	const vehicleConfirmed = Boolean(run?.vehicle?.id && run?.vehicleConfirmed);
	steps.push({
		key: 'vehicle',
		label: 'Confirm vehicle',
		description: vehicleConfirmed
			? 'Vehicle confirmed.'
			: 'Assign and confirm the vehicle you are driving today.',
		status: vehicleConfirmed ? MY_DAY_STEP_STATUS.DONE : MY_DAY_STEP_STATUS.TODO,
		required: true,
	});

	const accessPreparationRows = buildAccessPreparationRows(dockets);
	const accessKeysConfirmation = getMyDayConfirmation(run, MY_DAY_CONFIRMATION_KEY.ACCESS_KEYS);
	const accessKeysConfirmed = Boolean(accessKeysConfirmation);
	const notPacked = describeUntickedItems('Not packed', getRunAccessItems(dockets), accessKeysConfirmation);
	steps.push({
		key: 'accessKeys',
		label: 'Keys and site access',
		description: accessKeysConfirmed
			? `Keys and site access requirements confirmed.${notPacked ? ` ${notPacked}` : ''}`
			: 'Confirm you have keys, FOBs and access passes, and review site sign-in requirements for these stops.',
		status: !accessPreparationRows.length
			? MY_DAY_STEP_STATUS.HIDDEN
			: accessKeysConfirmed
				? MY_DAY_STEP_STATUS.DONE
				: MY_DAY_STEP_STATUS.TODO,
		required: true,
		badgeCount: accessPreparationRows.length || undefined,
		skipWhenRunStarted: true,
	});

	const pickPreparationDockets = dockets.filter(docket => !isServiceDocketLabelPrintOnly(docket));
	const docketsWithOutstandingPicks = pickPreparationDockets.filter(docketHasOutstandingPick);
	const partiallyPickedCount = docketsWithOutstandingPicks.filter(docketIsPartiallyPicked).length;
	const hasSalesSinceRecalculation = pickPreparationDockets.some(docket => docket?.pickList?.hasProductSoldSinceLastPickListRecalculation);
	const hasStalePicklist = Boolean(todayDateKey) && pickPreparationDockets.some(docket => {
		const calculatedAt = docket?.pickList?.calculatedAt;
		if (!calculatedAt) {
			return false;
		}
		return getLocalTime(timezone, calculatedAt).format('YYYY-MM-DD') < todayDateKey;
	});
	const hasAnyPickList = pickPreparationDockets.some(docket => docket?.pickList?.id);
	const requiresPickPreparation = hasAnyPickList || pickPreparationDockets.some(docket => (
		shouldMachineBeRestocked(docket?.machineServiceType)
	));
	const locallyPicksUpToDate = hasAnyPickList &&
		!docketsWithOutstandingPicks.length &&
		!hasSalesSinceRecalculation &&
		!hasStalePicklist;
	const picksUpToDate = run?.myDayPickTopUpStatus?.picksUpToDate === undefined
		? locallyPicksUpToDate
		: Boolean(run.myDayPickTopUpStatus.picksUpToDate) && !docketsWithOutstandingPicks.length;
	const topUpNotNeededConfirmed = (
		!picksUpToDate &&
		!docketsWithOutstandingPicks.length &&
		Boolean(run?.myDayPickTopUpStatus?.markedNotNeeded)
	);
	const pickPreparationReady = picksUpToDate || topUpNotNeededConfirmed;
	const tubsLoadedConfirmed = isMyDayStepConfirmed(run, MY_DAY_CONFIRMATION_KEY.TUBS_LOADED);
	const runStarted = isServiceRunStarted(run);
	// Once the driver has departed with confirmed tubs, later machine sales must
	// not pull an active run back into the warehouse-preparation flow.
	const pickPreparationLocked = runStarted && tubsLoadedConfirmed;
	const packAndPickDone = pickPreparationLocked || (pickPreparationReady && tubsLoadedConfirmed);
	let packAndPickDescription = 'Confirm you have loaded all tubs into the van.';
	if (packAndPickDone) {
		packAndPickDescription = pickPreparationLocked
			? 'Pick preparation is complete and all tubs are loaded.'
			: topUpNotNeededConfirmed
				? 'Top-up was marked not needed and all tubs are loaded.'
				: 'All picks are up to date and tubs are loaded.';
	}
	else if (!pickPreparationReady && partiallyPickedCount) {
		packAndPickDescription = `${partiallyPickedCount} ${partiallyPickedCount === 1 ? 'pick is' : 'picks are'} part picked and still ${partiallyPickedCount === 1 ? 'needs' : 'need'} finishing. Recalculate picklists and finish all outstanding items before loading.`;
	}
	else if (!pickPreparationReady && docketsWithOutstandingPicks.length) {
		packAndPickDescription = 'Recalculate picklists and pick any outstanding items before loading.';
	}
	else if (!pickPreparationReady) {
		packAndPickDescription = 'All current picklists are fully picked, but Sentry detected a newer sale or an older calculation. Recalculate if a top-up is needed, or mark top-up not needed.';
	}
	else if (topUpNotNeededConfirmed) {
		packAndPickDescription = 'Top-up was marked not needed. Confirm you have loaded all tubs into the van.';
	}
	const canMarkTopUpNotNeeded = (
		!pickPreparationLocked &&
		Boolean(run?.myDayPickTopUpStatus?.canMarkNotNeeded) &&
		!docketsWithOutstandingPicks.length &&
		!pickPreparationReady
	);
	const canConfirmTubsLoaded = pickPreparationReady && !tubsLoadedConfirmed;
	steps.push({
		key: 'packAndPick',
		label: 'Recalculate and top up pick',
		description: packAndPickDescription,
		status: !requiresPickPreparation
			? MY_DAY_STEP_STATUS.HIDDEN
			: packAndPickDone
				? MY_DAY_STEP_STATUS.DONE
				: MY_DAY_STEP_STATUS.TODO,
		required: true,
		badgeCount: docketsWithOutstandingPicks.length || undefined,
		canMarkTopUpNotNeeded,
		canConfirmTubsLoaded,
		topUpNotNeededConfirmed,
		// Preserve legacy mid-run handling while keeping a confirmed departure
		// complete when later sales arrive.
		skipWhenRunStarted: pickPreparationReady || tubsLoadedConfirmed,
	});

	const ticketsCount = dockets.reduce((sum, docket) => sum + (Number(docket?.driverTicketCount) || 0), 0);
	const ticketsSummaryConfirmed = isMyDayStepConfirmed(run, MY_DAY_CONFIRMATION_KEY.TICKETS_SUMMARY);
	steps.push({
		key: 'ticketsSummary',
		label: 'Tickets summary',
		description: ticketsSummaryConfirmed
			? 'Tickets read and packed for.'
			: 'Read today\'s tickets and confirm before leaving the warehouse.',
		status: !ticketsCount
			? MY_DAY_STEP_STATUS.HIDDEN
			: ticketsSummaryConfirmed
				? MY_DAY_STEP_STATUS.DONE
				: MY_DAY_STEP_STATUS.TODO,
		required: true,
		badgeCount: ticketsCount || undefined,
		skipWhenRunStarted: true,
	});

	const requireLeaveWarehouseChecklist = Boolean(tenantSettings?.myDayRequireLeaveWarehouseChecklist);
	const leaveWarehouse = hasSubmissionForPurpose({
		checklistSubmissions,
		pendingChecklistPurposes,
		purpose: CHECKLIST_PURPOSE.LEAVE_WAREHOUSE,
	});
	steps.push({
		key: 'leaveWarehouseChecklist',
		label: 'Leave warehouse checklist',
		description: leaveWarehouse.pending
			? 'Queued offline — will sync automatically.'
			: leaveWarehouse.done
				? getChecklistSignOffDescription(leaveWarehouse.submission, timezone) || 'Checklist submitted.'
				: 'Complete the checklist right before you leave the warehouse.',
		// 'waiting' or 'signedOff' for checklists that need manager sign-off, else undefined.
		signOffState: getChecklistSignOff(leaveWarehouse.submission)?.state,
		status: leaveWarehouse.done
			? MY_DAY_STEP_STATUS.DONE
			: MY_DAY_STEP_STATUS.TODO,
		required: requireLeaveWarehouseChecklist,
	});

	return steps;
};

/**
 * Per-stop guided steps for one docket, extracted unchanged from the previous
 * DriverMyDayView `steps` useMemo. `helpers` carries device-local state the
 * pure module cannot derive: `{ skippedPickStep, skippedRestockStep,
 * hasPendingConfirmStockSync, isDeviceOffline }`.
 * Press handlers are attached by the caller, keyed on step `id`.
 */
export const buildPerStopSteps = (docket, helpers = {}) => {
	if (!docket) {
		return [];
	}

	const {
		skippedPickStep,
		skippedRestockStep,
		hasPendingConfirmStockSync,
		hasPendingConfirmValuesSync,
		isDeviceOffline,
	} = helpers;
	const isOffline = docket.machine?.isOffline ?? !docket.machine?.commDevices?.length;
	const requiresRestock = shouldMachineBeRestocked(docket.machineServiceType);
	const requiresPick = requiresRestock && !isServiceDocketLabelPrintOnly(docket);
	const supportsStockConfirmation = shouldConfirmStockLevels({
		machineUsesIngredients: docket.machine?.usesIngredients,
		isOffline,
	});
	const requiresTicketStep = shouldMachineBeRepaired(docket.machineServiceType) &&
		docket.driverTicketCount > 0 &&
		Boolean(docket.ticketList?.id);
	const inventoryConfirmed = isInventoryConfirmed(docket.inventoryCheck);
	const confirmValuesCompleted = (docket.machine?.usesIngredients
		? Boolean(docket.isIngredientRestock)
		: inventoryConfirmed) || Boolean(hasPendingConfirmValuesSync);
	const confirmStockCompleted = inventoryConfirmed || Boolean(hasPendingConfirmStockSync);
	const isPartiallyPicked = docketIsPartiallyPicked(docket);

	const builtSteps = [];

	if (requiresPick && !isOffline && docket.pickList?.id) {
		builtSteps.push({
			id: 'pick',
			label: isPartiallyPicked ? 'Pick list – part picked' : 'Pick list',
			description: isPartiallyPicked
				? 'This pick was only partly finished. Pick the remaining required products for this stop.'
				: 'Pick all required products for this stop.',
			done: Boolean(docket.inventoryPicked || skippedPickStep),
		});
	}

	if (requiresRestock && !isOffline) {
		const isMarket = docket.machine?.type === POS_TYPE.MARKET;
		const supportsRestockComm = !isMarket;
		const isWaitingForRestockComm = Boolean(supportsRestockComm && docket.startedProcessing && !docket.isRestocked);
		// With no connection the phone cannot see the machine's restock update
		// even though the machine reports to the server on its own — tell the
		// driver the truth and let the board offer to continue.
		const restockDescription = isWaitingForRestockComm
			? (isDeviceOffline
				? 'No signal — the restock update will appear when you are back in reception. You can continue and check later.'
				: 'Machine is processing. This step updates automatically when communication arrives.')
			: isMarket
				? 'Open restock and complete manual market restock.'
				: isDeviceOffline
					? 'No signal — restock manually if needed, or continue and check when back in reception.'
					: 'Open restock and wait for communication to be received.';

		builtSteps.push({
			id: 'restock',
			label: isWaitingForRestockComm ? 'Waiting for restock comm' : 'Restock',
			description: restockDescription,
			done: Boolean(docket.isRestocked || skippedRestockStep),
			isWaiting: isWaitingForRestockComm,
			// Comm machines offline: the guided step can be skipped locally; the
			// machine's restock still records server-side when connection returns.
			canSkipOffline: Boolean(supportsRestockComm && isDeviceOffline && !docket.isRestocked),
		});
	}

	if (requiresRestock && (docket.machine?.usesIngredients || isOffline)) {
		builtSteps.push({
			id: 'confirm_values',
			label: 'Confirm values',
			description: hasPendingConfirmValuesSync
				? 'Values confirmation queued offline. It will sync automatically.'
				: 'Confirm restock values for ingredient or offline assets.',
			done: confirmValuesCompleted,
		});
	}
	else if (supportsStockConfirmation) {
		const stockConfirmationBlocked = Boolean(!confirmStockCompleted &&
			requiresRestock &&
			!docket.isRestocked);
		builtSteps.push({
			id: 'confirm_stock',
			required: isStockConfirmationRequired(docket),
			label: 'Confirm stock levels',
			description: stockConfirmationBlocked
				? 'Complete a manual restock or wait for the DEX restock allocation before confirming stock.'
				: hasPendingConfirmStockSync
					? 'Stock confirmation queued offline. It will sync automatically.'
					: requiresRestock
						? 'Confirm stock quantities after restock.'
						: 'Remove any expired products and confirm current stock quantities.',
			done: confirmStockCompleted,
			blocked: stockConfirmationBlocked,
		});
	}

	if (requiresTicketStep) {
		builtSteps.push({
			id: 'tickets',
			label: `Tickets (${docket.driverTicketCount})`,
			description: 'Resolve all driver-visible tickets for this stop.',
			done: Boolean(docket.ticketsResolved),
		});
	}

	if (docket.requirePhoto) {
		builtSteps.push({
			id: 'photos',
			label: 'Photos',
			description: 'Take required machine or market photos.',
			done: Boolean(docket.photoTaken),
		});
	}

	return builtSteps;
};

export const buildEndOfDaySteps = ({
	run,
	sortedDockets,
	checklistSubmissions,
	pendingChecklistPurposes,
	tenantSettings,
	clock,
	clockOutReminder,
	allAssignedRunsComplete,
	timezone,
}) => {
	const dockets = sortedDockets || run?.serviceDockets || [];
	const steps = [];
	const returnedToWarehouseAt = clockOutReminder?.returnedToWarehouseAt;
	const showReturnedToWarehouse = Boolean(returnedToWarehouseAt || (
		allAssignedRunsComplete && clock?.isClockedIn
	));
	steps.push({
		key: 'returnedToWarehouse',
		label: 'Returned to warehouse',
		description: returnedToWarehouseAt
			? clock?.isClockedIn && clockOutReminder?.reminderActive
				? 'You are still clocked in. Please clock out now.'
				: 'Return confirmed. Sentry will remind you and your manager if you are still clocked in after 20 minutes.'
			: clockOutReminder?.canConfirmReturnedToWarehouse
				? 'Confirm once you are back. Sentry will remind you if you are still clocked in after 20 minutes.'
				: 'Checking that every assigned run is complete.',
		status: !showReturnedToWarehouse
			? MY_DAY_STEP_STATUS.HIDDEN
			: returnedToWarehouseAt
				? MY_DAY_STEP_STATUS.DONE
				: MY_DAY_STEP_STATUS.TODO,
		required: Boolean(showReturnedToWarehouse && clock?.isClockedIn && !returnedToWarehouseAt),
		canConfirm: Boolean(clockOutReminder?.canConfirmReturnedToWarehouse),
	});

	const moneyBagDockets = dockets.filter(docketRequiresMoneyBagReturn);
	const moneyBagsConfirmed = isMyDayStepConfirmed(run, MY_DAY_CONFIRMATION_KEY.MONEY_BAGS);
	steps.push({
		key: 'moneyBags',
		label: 'Money bags return',
		description: moneyBagsConfirmed
			? 'Money bags returned.'
			: 'Count your money bags, place them on the return pile and confirm.',
		status: !moneyBagDockets.length
			? MY_DAY_STEP_STATUS.HIDDEN
			: moneyBagsConfirmed
				? MY_DAY_STEP_STATUS.DONE
				: MY_DAY_STEP_STATUS.TODO,
		required: true,
		badgeCount: moneyBagDockets.length || undefined,
	});

	// Keys, passes and PPE to hand back (site sign-in returns nothing), shown once
	// they went out: keys packed at start of day or the run started. Never required.
	const accessItems = getRunAccessItems(dockets);
	const returnKeyStopCount = new Set(accessItems.map(item => item.docket.id)).size;
	const keysWentOut = isMyDayStepConfirmed(run, MY_DAY_CONFIRMATION_KEY.ACCESS_KEYS) || isServiceRunStarted(run);
	const returnKeysConfirmation = getMyDayConfirmation(run, MY_DAY_CONFIRMATION_KEY.RETURN_KEYS);
	const returnKeysConfirmed = Boolean(returnKeysConfirmation);
	const notReturned = describeUntickedItems('Not returned', accessItems, returnKeysConfirmation);
	steps.push({
		key: 'returnKeys',
		label: 'Return keys and access passes',
		description: returnKeysConfirmed
			? notReturned || 'Keys and passes returned.'
			: `Hand back what you took for ${returnKeyStopCount} ${returnKeyStopCount === 1 ? 'stop' : 'stops'}.`,
		status: !returnKeyStopCount || !keysWentOut
			? MY_DAY_STEP_STATUS.HIDDEN
			: returnKeysConfirmed
				? MY_DAY_STEP_STATUS.DONE
				: MY_DAY_STEP_STATUS.TODO,
		required: false,
	});

	const returnStockConfirmed = isMyDayStepConfirmed(run, MY_DAY_CONFIRMATION_KEY.RETURN_STOCK);
	steps.push({
		key: 'returnStock',
		label: 'Return stock',
		description: returnStockConfirmed
			? 'Stock returned.'
			: 'Return in-date stock to the warehouse floor, expired stock to the designated tubs, then confirm.',
		status: returnStockConfirmed ? MY_DAY_STEP_STATUS.DONE : MY_DAY_STEP_STATUS.TODO,
		required: true,
	});

	const requireVehicleChecklist = Boolean(tenantSettings?.myDayRequireVehicleChecklist);
	const vehicleChecklist = hasSubmissionForPurpose({
		checklistSubmissions,
		pendingChecklistPurposes,
		purpose: CHECKLIST_PURPOSE.VEHICLE_END_OF_DAY,
	});
	steps.push({
		key: 'vehicleChecklist',
		label: 'Vehicle checklist',
		description: vehicleChecklist.pending
			? 'Queued offline — will sync automatically.'
			: vehicleChecklist.done
				? getChecklistSignOffDescription(vehicleChecklist.submission, timezone) || 'Vehicle check submitted.'
				: 'Complete the checklist, ask a manager to inspect the vehicle, then select them to sign it off.',
		signOffState: getChecklistSignOff(vehicleChecklist.submission)?.state,
		status: vehicleChecklist.done ? MY_DAY_STEP_STATUS.DONE : MY_DAY_STEP_STATUS.TODO,
		required: requireVehicleChecklist,
	});

	const selfDebriefMode = tenantSettings?.myDaySelfDebriefMode || MY_DAY_SELF_DEBRIEF_MODE.OPTIONAL;
	const selfDebrief = hasSubmissionForPurpose({
		checklistSubmissions,
		pendingChecklistPurposes,
		purpose: CHECKLIST_PURPOSE.SELF_DEBRIEF,
	});
	steps.push({
		key: 'selfDebrief',
		label: 'Debrief',
		description: selfDebrief.pending
			? 'Queued offline — will sync automatically.'
			: selfDebrief.done
				? getChecklistSignOffDescription(selfDebrief.submission, timezone) || 'Self debrief submitted.'
				: 'Debrief with your manager, or complete the self debrief if they are not around.',
		signOffState: getChecklistSignOff(selfDebrief.submission)?.state,
		status: selfDebriefMode === MY_DAY_SELF_DEBRIEF_MODE.OFF
			? MY_DAY_STEP_STATUS.HIDDEN
			: selfDebrief.done
				? MY_DAY_STEP_STATUS.DONE
				: MY_DAY_STEP_STATUS.TODO,
		required: isSelfDebriefRequired(selfDebriefMode),
	});

	const requiredEndStepsIncomplete = steps.some(step => (
		step.required && step.status === MY_DAY_STEP_STATUS.TODO
	));
	const clockedOut = Boolean(!clock?.isClockedIn && clock?.hasClockedOutToday);
	steps.push({
		key: 'clockOut',
		label: 'Clock out',
		description: clockedOut
			? 'You are clocked out.'
			: clockOutReminder?.reminderActive
				? 'You are still clocked in. Open shift pass and clock out now.'
				: requiredEndStepsIncomplete
					? 'Finish the required end-of-day steps before clocking out.'
					: 'Clock out on the kiosk before you leave.',
		status: clockedOut ? MY_DAY_STEP_STATUS.DONE : MY_DAY_STEP_STATUS.TODO,
		// While the driver is clocked in the board must keep prompting clock-out as
		// the next action instead of showing "Day complete" (it is never a hard block).
		required: Boolean(clock?.isClockedIn),
	});

	return steps;
};

export const getNextAction = ({ startSteps, activeDocket, perStopSteps, endSteps, runStarted }) => {
	// Required preparation steps still pull the driver back. Attendance remains
	// a separate visible exception and never becomes an operational gate.
	const nextStartStep = (startSteps || []).find(step => (
		step.required &&
		step.blocksProgress !== false &&
		step.status === MY_DAY_STEP_STATUS.TODO &&
		(!runStarted || !step.skipWhenRunStarted)
	));
	if (nextStartStep) {
		return { type: 'start', step: nextStartStep };
	}

	if (!runStarted && activeDocket) {
		return {
			type: 'startRun',
			step: {
				key: 'startServiceRun',
				label: 'Start service run',
				description: 'Everything is ready. Start servicing your first stop.',
			},
		};
	}

	if (runStarted && activeDocket) {
		// An incomplete stop keeps the current-stop focus even when it has no
		// guided sub-steps left (e.g. repair-only visits) — end-of-day steps must
		// not surface while stops remain.
		const nextPerStopStep = (perStopSteps || []).find(step => step.required !== false && !step.done);
		return { type: 'stop', step: nextPerStopStep || null };
	}

	if (runStarted) {
		const nextEndStep = (endSteps || []).find(step => (
			step.required && step.status === MY_DAY_STEP_STATUS.TODO
		));
		if (nextEndStep) {
			return { type: 'end', step: nextEndStep };
		}
	}

	return null;
};

export const getDriverDayPhase = ({ run, clock, checklistSubmissions, requirements }) => {
	const dockets = run?.serviceDockets || [];
	const allDocketsComplete = areAllDocketsComplete(dockets);
	const anyDocketStarted = isServiceRunStarted(run);

	if (allDocketsComplete) {
		const vehicleChecklistSatisfied = !requirements?.vehicleChecklist ||
			hasSubmissionForPurpose({ checklistSubmissions, purpose: CHECKLIST_PURPOSE.VEHICLE_END_OF_DAY }).done;
		const selfDebriefRequired = requirements?.selfDebriefMode !== undefined
			? isSelfDebriefRequiredForRun(run, requirements.selfDebriefMode)
			: Boolean(requirements?.selfDebrief);
		const selfDebriefSatisfied = !selfDebriefRequired ||
			hasSubmissionForPurpose({ checklistSubmissions, purpose: CHECKLIST_PURPOSE.SELF_DEBRIEF }).done;

		if (vehicleChecklistSatisfied && selfDebriefSatisfied && clock?.hasClockedOutToday) {
			return DRIVER_DAY_PHASE.DAY_CLOSED;
		}

		return DRIVER_DAY_PHASE.STOPS_DONE;
	}

	if (anyDocketStarted) {
		return DRIVER_DAY_PHASE.ON_THE_RUN;
	}

	if (clock?.isClockedIn) {
		return DRIVER_DAY_PHASE.PREPARING;
	}

	return DRIVER_DAY_PHASE.NOT_STARTED;
};
